import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  Button,
  Popover,
  PopoverBody,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import HelpIcon from '../HelpIcon';

import {
  userCan,
  userIsAdmin,
} from '../../helpers/Auth';
import { getSetting } from '../../helpers/Settings';

const AccessModalAccessTypeDropdown = ({
  mode,
  accessType,
  radioName,
  labels,
  openSettings,
  handleChangeAccessType,
}) => {
  const intl = useIntl();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen(prevState => !prevState);

  const publicText = intl.formatMessage({ defaultMessage: 'Visible to all users in your workspace' });
  const restrictedText = intl.formatMessage({ defaultMessage: 'Visible only to users and Teams you add' });

  let publicEnabled = userIsAdmin();
  if (mode === 'collections') {
    publicEnabled = userCan('collection.create_public');
  }
  if (mode === 'files') {
    publicEnabled = userCan('file.create_public');
  }

  let modeText = mode;
  if (mode === 'collections') {
    modeText = intl.formatMessage({ defaultMessage: 'Collections' });
  }

  const icon = accessType === 'public' ? 'eye' : 'lock';

  const hangeAccessType = (e) => {
    toggle();
    handleChangeAccessType(e);
  };

  return (
    <div className="access-type">
      <div className={classnames('access-type-form', `access-type-form-type-${accessType}`)}>
        <div className="access-type-icon">
          <div className="user-avatar share-avatar">
            <div className="avatar avatar-40 icon-avatar outline-avatar primary">
              <i className={`fas fa-${icon}`} />
            </div>
          </div>
        </div>
        <div id="access-type-select" className="access-type-select">
          <h5 className="access-type-title">
            {
              accessType === 'public' ?
                intl.formatMessage({ defaultMessage: 'Open' }) :
                intl.formatMessage({ defaultMessage: 'Restricted' })
            }
            {' '}
            <i className={`fas fa-${dropdownOpen ? 'chevron-up' : 'chevron-down'}`} />
          </h5>
          <p className="access-type-details">
            {accessType === 'public' ? publicText : restrictedText}
          </p>
        </div>
        <Popover
          popperClassName="access-type-dropdown"
          placement="bottom-start"
          trigger="legacy"
          isOpen={dropdownOpen}
          target="access-type-select"
          boundariesElement="body"
          delay={0}
          fade={false}
          hideArrow={true}
          positionFixed={true}
          offset={15}
          toggle={toggle}
        >
          <PopoverBody>
            {!(getSetting('disable_public_files') && mode === 'files') && (
              <FormGroup check>
                <Label className="access-type-item" check>
                  <Input
                    type="radio"
                    name={radioName}
                    value="public"
                    checked={accessType === 'public'}
                    disabled={!publicEnabled && accessType !== 'public'}
                    onChange={hangeAccessType}
                  />
                  {' '}
                  <h5 className="access-type-title">
                    {intl.formatMessage({ defaultMessage: 'Open' })}
                    {(!publicEnabled && accessType !== 'public') ? (
                      <HelpIcon id="public-no-permissions-tooltip">
                        {intl.formatMessage({
                          defaultMessage: `You do not have permissions to create Open {modeText}`,
                        }, { modeText })}
                      </HelpIcon>
                    ) : (
                      <>
                        {labels === 'tooltips' && (
                          <HelpIcon id="access-public-tooltip" placement="right">
                            {publicText}
                          </HelpIcon>
                        )}
                      </>
                    )}
                  </h5>
                  {labels === 'labels' && (
                    <p className="access-type-details">
                      {publicText}
                    </p>
                  )}
                </Label>
              </FormGroup>
            )}
            <FormGroup check>
              <Label className="access-type-item" check>
                <Input
                  type="radio"
                  name={radioName}
                  value="private"
                  checked={accessType === 'private'}
                  onChange={hangeAccessType}
                />
                {' '}
                <h5 className="access-type-title">
                  {intl.formatMessage({ defaultMessage: 'Restricted' })}
                  {labels === 'tooltips' && (
                    <HelpIcon id="access-private-tooltip" placement="right">
                      {restrictedText}
                    </HelpIcon>
                  )}
                </h5>
                {labels === 'labels' && (
                  <p className="access-type-details">
                    {restrictedText}
                  </p>
                )}
              </Label>
            </FormGroup>
          </PopoverBody>
        </Popover>
        <div className="access-type-settings">
          {mode === 'collections' && (
            <Button color="link" onClick={openSettings}>
              <i className="far fa-cog" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

AccessModalAccessTypeDropdown.propTypes = {
  mode: PropTypes.string.isRequired,
  accessType: PropTypes.string,
  radioName: PropTypes.string,
  labels: PropTypes.string,
  openSettings: PropTypes.func.isRequired,
  handleChangeAccessType: PropTypes.func.isRequired,
};

AccessModalAccessTypeDropdown.defaultProps = {
  accessType: null,
  radioName: 'access-type',
  labels: 'labels',
};

export default AccessModalAccessTypeDropdown;
